"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { ContactDetails } from "@/components/contact-details"
import { ImportContactDialog } from "@/components/import-contact-dialog"
import { useToast } from "@/hooks/use-toast"
import { ContactDialog } from "@/components/create-contact-dialog"
import { CreateRecipientListDialog } from "@/components/create-recipient-list-dialog"
import { ManageContactListsDialog } from "@/components/manage-contact-lists-dialog"
import { ActionConfirmDialog } from "@/components/ui/confirm-dialog"
import { AddStatusDialog } from "@/components/AddStatusDialog"
import { DisableContactDialog } from "@/components/DisableContactDialog"
import ContactTable from "@/components/contactsPage/ContactTable"
import { ContactsHeader } from "@/components/contactsPage/ContactsHeader"
import { ContactsPagination } from "@/components/contactsPage/ContactsPagination"
import { useContacts } from "@/hooks/useContacts"
import { useContactSelection } from "@/hooks/useContactSelection"
import { useContactActions } from "@/hooks/useContactActions"
import { useDebounce } from "@/hooks/useDebounce"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ShieldCheck, AlertCircle, XCircle, HelpCircle, MailX, UserMinus } from "lucide-react"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { EMAIL_STATUS } from "@/utils/email-status"

interface Contact {
  id: string
  email: string
  name: string
  source: string
  location?: string
  status: any
  remarks?: string
  createdAt: string
  updatedAt: string
  isActive: boolean
  emailStatus?: string
  [key: string]: any
}

export default function ContactsPage() {
  // Custom hooks
  const { contacts, loading, error, pagination, fetchContacts, refreshContacts,
    // master data to show in dropdown
    statusOptionsList,
    sourceOptionsList,
    fetchMasterData,
    statusFilter,
    setStatusFilter,
    sourceFilter,
    setSourceFilter,
    locationFilter,
    setLocationFilter,
    handleLimitChange,
    emailStatusFilter,
    setEmailStatusFilter,
    emailStatusStats,
    listsFilter,
    setListsFilter,
    recipientListsList,
    fetchRecipientLists,
    handleArchiveMultiple
  } = useContacts()
  const {
    selectedContacts,
    pages,
    handleSelectContact,
    togglePageCheck,
    clearSelection,
    setSelectedContacts
  } = useContactSelection()
  const { exporting, archivingContacts, handleExport, handleArchive } = useContactActions()

  // Local state
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilterOptions, setShowFilterOptions] = useState(false)

  // Dialog states
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false)
  const [showCreateRecipientListDialog, setShowCreateRecipientListDialog] = useState(false)
  const [showManageListsDialog, setShowManageListsDialog] = useState(false)
  const [showEditContactDialog, setShowEditContactDialog] = useState(false)
  const [showAddStatusDialog, setShowAddStatusDialog] = useState(false)
  const [showDisableContactDialog, setShowDisableContactDialog] = useState(false)
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)

  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null)
  const [contactToArchive, setContactToArchive] = useState<Contact | null>(null)

  const { toast } = useToast()
  const debouncedSearch = useDebounce(searchTerm, 500)

  // Search effect
  useEffect(() => {
    fetchContacts(1, debouncedSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter)
  }, [debouncedSearch, statusFilter, sourceFilter, locationFilter, emailStatusFilter, listsFilter])

  // Archive handler
  const confirmArchiveContact = async () => {
    if (!contactToArchive) return

    const success = await handleArchive(contactToArchive)
    if (success) {
      await refreshContacts()
    }

    setShowArchiveDialog(false)
    setContactToArchive(null)
  }
  // Fetch master data when contacts page opens
  useEffect(() => {
    fetchMasterData();
    fetchRecipientLists();
  }, [])

  const handleContactsExport = () => {
    handleExport(debouncedSearch, statusFilter, sourceFilter, locationFilter, listsFilter, emailStatusFilter)
  }

  const onArchive = async () => {
    const ids = selectedContacts.map((contact) => contact.id)
    await handleArchiveMultiple(ids)
    clearSelection()
  }

  // useEffect(() => {
  //   console.log("selectedContact", selectedContact)
  // }, [selectedContact])

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              Error Loading Contacts
            </h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={refreshContacts}>Retry</Button>
          </div>
        </div>
      </div>
    )
  }

  const emailStatusCards = [
    { id: "safe", count: emailStatusStats?.safe || 0 },
    { id: "risky", count: emailStatusStats?.risky || 0 },
    { id: "invalid", count: emailStatusStats?.invalid || 0 },
    { id: "unverified", count: emailStatusStats?.unverified || 0 },
    { id: "bounced", count: emailStatusStats?.bounced || 0 },
    { id: "unsubscribed", count: emailStatusStats?.unsubscribed || 0 },
  ];

  return (
    <div className="animate-in slide-in-from-bottom-6 fade-in duration-1000 ease-out">
      <div className="">
        <ContactsHeader
          setShowImportDialog={setShowImportDialog}
          selectedContacts={selectedContacts}
          setSelectedContacts={setSelectedContacts}
          setStatusFilter={setStatusFilter}
          setSourceFilter={setSourceFilter}
          setLocationFilter={setLocationFilter}
          setListsFilter={setListsFilter}
          statusOptionsList={statusOptionsList}
          sourceOptionsList={sourceOptionsList}
          recipientListsList={recipientListsList}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          loading={loading}
          selectedCount={selectedContacts.length}
          exporting={exporting}
          onExport={handleContactsExport}
          onAddNew={() => setShowCreateContactDialog(true)}
          onAddStatus={() => setShowAddStatusDialog(true)}
          onAddToList={() => setShowCreateRecipientListDialog(true)}
          onDisable={() => setShowDisableContactDialog(true)}
          onToggleFilter={() => setShowFilterOptions(!showFilterOptions)}
          showFilterOptions={showFilterOptions}
          onArchive={onArchive}
        />
      </div>

      {/* Email Status Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {emailStatusCards.map(({ id, count }) => {
          const status = EMAIL_STATUS[id as keyof typeof EMAIL_STATUS];
          const Icon = status.icon;
          
          return (
            <motion.div
              key={id}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() =>
                setEmailStatusFilter(emailStatusFilter === id ? "" : id)
              }
              className={cn(
                "cursor-pointer p-4 rounded-xl border transition-all duration-200 flex items-center gap-4",
                status.card.bg,
                status.card.border,
                status.card.hover,
                emailStatusFilter === id
                  ? "ring-2 ring-primary ring-offset-2 dark:ring-offset-zinc-950 shadow-md scale-[1.02]"
                  : "shadow-sm"
              )}
            >
              <div
                className={cn(
                  "p-2.5 rounded-lg bg-white/90 dark:bg-zinc-900/90 shadow-sm transition-colors",
                  status.card.color
                )}
              >
                <Icon className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest opacity-80">
                  {status.label}
                </p>

                <h4 className="text-2xl font-extrabold tracking-tight text-zinc-900 dark:text-zinc-50">
                  {count.toLocaleString()}
                </h4>
              </div>
            </motion.div>
          );
        })}
      </div>

      <ScrollArea className="h-[calc(100vh-280px)] rounded-md border">
        <ContactTable
          listsFilter={listsFilter}
          contacts={contacts}
          loading={loading}
          togglePageCheck={togglePageCheck}
          pages={pages}
          pagination={pagination}
          setShowCreateContactDialog={setShowCreateContactDialog}
          setSelectedContacts={setSelectedContacts}
          selectedContacts={selectedContacts}
          setSelectedContact={setSelectedContact}
          setContactToArchive={setContactToArchive}
          setShowArchiveDialog={setShowArchiveDialog}
          setContactToEdit={setContactToEdit}
          setShowEditContactDialog={setShowEditContactDialog}
          refreshContacts={refreshContacts}
        />
      </ScrollArea>

      <div className="px-6">
        <ContactsPagination
          pagination={pagination}
          onPageChange={(page) => fetchContacts(page, searchTerm)}
          onLimitChange={handleLimitChange}
        />
      </div>

      {/* Sheet for contact details */}
      <Sheet open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
        <SheetContent
          className="sm:max-w-[740px] p-0 overflow-hidden border-l border-white/5 dark:border-zinc-800/50 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-2xl shadow-2xl"
        >
          {selectedContact && (
            <div className="h-full flex flex-col">
              <ContactDetails contact={selectedContact} refreshContacts={refreshContacts} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* All dialogs */}
      <ImportContactDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        statusOptionsList={statusOptionsList}
        sourceOptionsList={sourceOptionsList}
        onContactsImported={(newContacts) => {
          refreshContacts()
          toast({
            title: "Contacts imported successfully",
            description: `${newContacts.length} contacts have been added`,
          })
        }}
      />

      <ContactDialog
        open={showCreateContactDialog || showEditContactDialog}
        onOpenChange={(open) => {
          if (showCreateContactDialog) setShowCreateContactDialog(open)
          if (showEditContactDialog) {
            setShowEditContactDialog(open)
            if (!open) setContactToEdit(null)
          }
        }}
        mode={showEditContactDialog ? "edit" : "create"}
        contact={contactToEdit}
        onContactCreated={() => {
          refreshContacts()
          toast({ title: "Contact created successfully" })
        }}
        onContactUpdated={() => {
          refreshContacts()
          setContactToEdit(null)
          setShowEditContactDialog(false)
          toast({ title: "Contact updated successfully" })
        }}
        statusOptionsList={statusOptionsList}
        sourceOptionsList={sourceOptionsList}
      />

      <CreateRecipientListDialog
        open={showCreateRecipientListDialog}
        onOpenChange={setShowCreateRecipientListDialog}
        selectedContacts={selectedContacts}
        onListCreated={(newList) => {
          clearSelection()
          toast({ title: `List "${newList.name}" created successfully` })
        }}
      />

      <ManageContactListsDialog
        open={showManageListsDialog}
        onOpenChange={setShowManageListsDialog}
        selectedContacts={selectedContacts}
        onContactsUpdated={() => {
          refreshContacts()
          toast({ title: "Lists updated successfully" })
        }}
      />

      <AddStatusDialog
        open={showAddStatusDialog}
        onOpenChange={setShowAddStatusDialog}
        selectedContacts={selectedContacts}
        onStatusUpdated={refreshContacts}
        statusOptionsList={statusOptionsList}
      />

      <DisableContactDialog
        open={showDisableContactDialog}
        onOpenChange={setShowDisableContactDialog}
        selectedContacts={selectedContacts}
        setSelectedContacts={setSelectedContacts}
        onStatusUpdated={refreshContacts}
      />

      <ImportContactDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        statusOptionsList={statusOptionsList}
        sourceOptionsList={sourceOptionsList}
        onContactsImported={(newContacts) => {
          // Refresh the contacts list after import
          fetchContacts(pagination.page, searchTerm)
          toast({
            title: "Contacts imported successfully",
            description: `${newContacts.length} contacts have been added to your list`,
          })
        }}
      />

      <ActionConfirmDialog
        open={showArchiveDialog}
        onOpenChange={setShowArchiveDialog}
        title="Archive Contact"
        description={
          <>
            Are you sure you want to archive "{contactToArchive?.name}" ({contactToArchive?.email})?
            <br /><br />
            <strong>This will:</strong><br />
            • Move the contact to the archive<br />
            • Hide it from the main contacts list<br />
            • Preserve all contact data<br />
            • Allow you to restore it later
          </>
        }
        actionText="Archive Contact"
        onConfirm={confirmArchiveContact}
      />
    </div>
  )
}
